import {
  activityTypeLabel,
  type CalendarEntry,
  type FreeSlot,
  formatBerlinDayMonth,
  formatBerlinWeekdayLong,
  fromBerlinDateTimeLocal,
  occupiesSlot,
  toBerlinDateTimeLocal,
} from '@praxi/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { CalendarGrid, type DropTarget } from '@/components/calendar-grid'
import { CalendarList } from '@/components/calendar-list'
import { CalendarMonth } from '@/components/calendar-month'
import { CalendarRail, type RailSelection } from '@/components/calendar-rail'
import { CalendarSidebar } from '@/components/calendar-sidebar'
import { SlotFinder, type SlotSearch } from '@/components/slot-finder'
import { SyncConflictBanner } from '@/components/sync-conflicts'
import { Button } from '@/components/ui/button'
import { calendarQueryOptions, freeSlotsQueryOptions, updateAppointment } from '@/lib/activities'
import { activityTypeListQueryOptions } from '@/lib/activity-types'
import { ApiError } from '@/lib/api'
import {
  addDays,
  isoWeek,
  monthLabel,
  shiftMonth,
  startOfWeek,
  todayInBerlin,
} from '@/lib/calendar-dates'
import { busyQueryOptions, googleConflictsQueryOptions } from '@/lib/google'
import { openingHoursQueryOptions } from '@/lib/settings'
import { strings } from '@/lib/strings'

/**
 * Nothing personal in the URL: an anchor date, the view and the slot status.
 *
 * Which entry is selected is deliberately not in here, the same call D8 made
 * for its expanded row — a selection belongs to the visit, not to the address.
 */
const searchSchema = z.object({
  /** `YYYY-MM-DD` in Berlin — the day, or the day the week is taken from. */
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  view: z.enum(['day', 'workweek', 'week', 'month', 'list']).optional(),
  /** The slot's status. What became of the treatment is the activity's status
   *  and is filtered on the Vorgänge page, where the list is the record. */
})

export const Route = createFileRoute('/_app/appointments')({
  validateSearch: searchSchema,
  component: CalendarPage,
})

/**
 * Five days by default (D9): the seven-day view is there for the Saturdays a
 * course falls on, and the working week is what one looks at.
 *
 * **All columns are the same width**, including the weekend. The design draws
 * Saturday and Sunday at about two thirds, and this used to cite that as the
 * design agreeing with the five-day default — the narrowing is a mistake in the
 * design and was struck from the correction list on instruction (D-K2). The
 * same holds for a day outside the opening hours: it is greyed, never narrowed.
 */
/**
 * The five views (D-K4). Three of them are the time grid, and two are not: the
 * month asks how full a fortnight is, the list asks what is coming — neither is
 * a question the grid answers well, which is why they draw nothing to scale.
 */
const VIEWS = ['day', 'workweek', 'week', 'month', 'list'] as const
type View = (typeof VIEWS)[number]

/** A month is drawn as six whole weeks, so it always has the same shape and a
 *  day never moves between rows as one pages through. */
const MONTH_DAYS = 42

const DAY_COUNT: Record<View, number> = {
  day: 1,
  workweek: 5,
  week: 7,
  month: MONTH_DAYS,
  list: 7,
}

function CalendarPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()

  const view: View = search.view ?? 'workweek'
  const anchor = search.date ?? todayInBerlin()
  const dayCount = DAY_COUNT[view]
  const firstDay =
    view === 'day'
      ? anchor
      : view === 'month'
        ? // The Monday on or before the first of the month.
          startOfWeek(`${anchor.slice(0, 7)}-01`)
        : startOfWeek(anchor)
  const days = Array.from({ length: dayCount }, (_, index) => addDays(firstDay, index))
  const lastDay = addDays(firstDay, dayCount)

  const from = fromBerlinDateTimeLocal(`${firstDay}T00:00`)
  const to = fromBerlinDateTimeLocal(`${lastDay}T00:00`)

  const calendarKey = calendarQueryOptions(from, to).queryKey
  const entries = useQuery(calendarQueryOptions(from, to))
  const types = useQuery(activityTypeListQueryOptions(true))

  /**
   * The practitioner's private calendars, as busy intervals and nothing else
   * — `freebusy.query` cannot answer with more, and the token's scope cannot
   * ask for more. Never stored: painted here and forgotten.
   *
   * It fails quietly. Without a connection or without a line the calendar
   * simply shows no foreign blocks, rather than covering a screen that
   * otherwise works with an error. The minute of `staleTime` is a cache and
   * not storage — it keeps paging through weeks with the arrow keys from
   * firing a request per keystroke.
   */
  const busy = useQuery({
    ...busyQueryOptions(from, to),
    // Only the time grid paints them, and a month would ask Google for six
    // weeks of intervals nothing draws.
    enabled: view !== 'month' && view !== 'list',
  })
  /** The weekly pattern, for the wash outside it. Painted, never enforced —
   *  see the prop's note in `CalendarGrid`. */
  const openingHours = useQuery(openingHoursQueryOptions)
  const conflicts = useQuery(googleConflictsQueryOptions)
  const conflicted = new Set((conflicts.data ?? []).map((entry) => entry.appointmentId))

  /* Everything in the window. The status chip row that used to narrow this is
     gone with K10 — the design has none, and a calendar hiding half its
     entries behind a filter is a calendar one cannot trust at a glance. */
  const shown = entries.data ?? []
  /**
   * Which day the rail describes: the one that was picked, if it is in view;
   * otherwise today, if *that* is in view; otherwise the first day of the
   * range (design). Without the last two steps the overview described a day
   * nobody was looking at — paging a week forward left it on the anchor, which
   * is no longer on screen.
   */
  const overviewDay =
    view === 'day' || days.includes(anchor)
      ? anchor
      : days.includes(todayInBerlin())
        ? todayInBerlin()
        : (days[0] ?? anchor)

  const occupied = new Set(
    (entries.data ?? [])
      .filter((entry) => occupiesSlot(entry.status))
      .map((entry) => toBerlinDateTimeLocal(entry.startsAt).slice(0, 10)),
  )

  const [selection, setSelection] = useState<RailSelection>(null)

  /**
   * What the open form currently describes, reported up from it (D-K3). Held
   * here rather than in the panel because two things need it: the grid, which
   * draws the block, and the sentence below, which is worked out against the
   * week this page has loaded and nobody else has.
   */
  const [draft, setDraft] = useState<{
    startsAt: string
    endsAt: string
    typeId: string
  } | null>(null)

  /**
   * Does the draft land on somebody? Advice, not a rule — migration 0034 took
   * the refusal out of the database on purpose, so this warns and the entry
   * saves anyway. It can only see the entries this window has loaded, which is
   * enough: the draft is inside that window by construction.
   */
  const clashes =
    draft !== null &&
    shown.some(
      (entry) =>
        occupiesSlot(entry.status) &&
        entry.startsAt < draft.endsAt &&
        entry.endsAt > draft.startsAt &&
        // Editing an entry does not collide with itself.
        (selection?.kind !== 'appointment' || entry.id !== selection.appointmentId),
    )

  /**
   * The slot finder (D9.5). Its range is the window on screen, so paging
   * forward is how one looks further — the answer stays in the grid, where a
   * time on a day is readable rather than merely describable.
   */
  const [slotSearch, setSlotSearch] = useState<SlotSearch | null>(null)
  const freeSlots = useQuery(
    freeSlotsQueryOptions(slotSearch ? { from, to, durationMin: slotSearch.durationMin } : null),
  )

  /**
   * The first gap on the overview day long enough for **what the practice
   * usually books** — the card in the rail.
   *
   * Which length that is comes from the catalogue rather than from a constant
   * here: the activity type marked as the default, if it carries a duration.
   * A number invented in this file (it was 60) is a claim about the practice
   * that the practice never made, and it produced a card promising room for an
   * hour in a diary that works in three quarters.
   *
   * **If the default type has no duration, there is nothing to ask** and the
   * card does not appear. The finder overrides it while a search is running,
   * so the card answers for whatever was picked on the left.
   */
  const defaultType = (types.data ?? []).find((entry) => entry.isDefault)
  const nextFreeSearch: SlotSearch | null =
    slotSearch ??
    (defaultType && defaultType.defaultDurationMin !== null
      ? { durationMin: defaultType.defaultDurationMin, typeId: defaultType.id }
      : null)

  /* Asked of the server like every other question about free time:
     `findFreeSlots` in the domain knows the opening hours and the private busy
     intervals, and a second answer computed in the browser would eventually
     disagree with the suggestions in the grid. */
  const nextFree = useQuery(
    freeSlotsQueryOptions(
      nextFreeSearch
        ? {
            from: fromBerlinDateTimeLocal(`${overviewDay}T00:00`),
            to: fromBerlinDateTimeLocal(`${addDays(overviewDay, 1)}T00:00`),
            durationMin: nextFreeSearch.durationMin,
          }
        : null,
    ),
  )

  /**
   * Moving an entry, optimistically — **and put back on refusal.**
   *
   * The rollback is the point, not the speed. With a toast alone the block
   * would stay where it was dropped while the database held the old time, and
   * some later refetch would move it back at a moment nobody connected with
   * the drop. Springing back is the answer arriving where the question was
   * asked.
   */
  const move = useMutation({
    mutationFn: (target: DropTarget) =>
      updateAppointment(target.appointmentId, {
        startsAt: target.startsAt,
        endsAt: target.endsAt,
      }),
    onMutate: async (target) => {
      await queryClient.cancelQueries({ queryKey: calendarKey })
      const previous = queryClient.getQueryData<CalendarEntry[]>(calendarKey)
      queryClient.setQueryData<CalendarEntry[]>(calendarKey, (current) =>
        (current ?? []).map((entry) =>
          entry.id === target.appointmentId
            ? { ...entry, startsAt: target.startsAt, endsAt: target.endsAt }
            : entry,
        ),
      )
      return { previous }
    },
    onError: (error, _target, context) => {
      if (context?.previous) queryClient.setQueryData(calendarKey, context.previous)
      toast.error(error instanceof ApiError ? error.message : strings.appointment.moveFailed)
    },
    onSuccess: () => toast.success(strings.appointment.moved),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['appointments'] })
      // The activity moved with it (see `updateAppointment` in the domain), so
      // every list that shows a date of service is stale too.
      await queryClient.invalidateQueries({ queryKey: ['activities'] })
    },
  })

  /**
   * Taking a suggestion: the form opens on that slot with the length and the
   * kind already set — the two things the search was about.
   *
   * **The search stays running** (D-K2). It used to be cleared here, which
   * meant a mistaken click cost the whole answer and the eight other times it
   * had found. It ends when the appointment is entered, when the same entry in
   * the rail is clicked again, or through "Auswahl aufheben" — three ways out,
   * none of them accidental.
   */
  const pickSlot = (slot: FreeSlot) => {
    setSelection({
      kind: 'new',
      startsAtLocal: toBerlinDateTimeLocal(slot.startsAt).slice(0, 16),
      ...(slotSearch?.typeId ? { typeId: slotSearch.typeId } : {}),
      ...(slotSearch ? { durationMin: slotSearch.durationMin } : {}),
      // A search by a bare duration was a search for a *Termin*: there is no
      // kind of treatment behind it, so the panel opens on the other tab.
      mode: slotSearch?.typeId ? 'activity' : 'appointment',
    })
  }

  const setSearch = (change: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (previous) => ({ ...previous, ...change }) })

  /** Opening an entry, from whichever of the three views it was clicked in. */
  const selectEntry = (entry: CalendarEntry) =>
    setSelection(
      entry.activityId
        ? { kind: 'activity', activityId: entry.activityId, appointmentId: entry.id }
        : // A calendar entry with no Vorgang behind it — which since D-K1 is a
          // thing one can create, and therefore a thing one has to be able to
          // open.
          { kind: 'appointment', appointmentId: entry.id },
    )

  const selectedEntryId =
    selection?.kind === 'activity' || selection?.kind === 'appointment'
      ? selection.appointmentId
      : null

  /**
   * What the header says about the range, in the design's forms:
   * `Mittwoch, 12. August` with `2026` beside it for a day, `10. – 14. August
   * 2026` with `KW 33` for a week or a list, and `August 2026` with nothing
   * beside it for a month — a calendar week means nothing to a range that
   * spans five of them.
   *
   * The long weekday, not the two-letter one from `strings.date.weekdays` —
   * that list belongs to column headings and the date picker, and reading it
   * as prose is what produced "Mi, 12.08.2026" (K9/K10).
   */
  const lastDayInView = addDays(firstDay, dayCount - 1)
  const title =
    view === 'day'
      ? formatBerlinWeekdayLong(`${anchor}T12:00:00Z`)
      : view === 'month'
        ? monthLabel(anchor)
        : `${Number(firstDay.slice(8, 10))}. – ${formatBerlinDayMonth(
            `${lastDayInView}T12:00:00Z`,
          )} ${lastDayInView.slice(0, 4)}`
  const subtitle =
    view === 'day'
      ? anchor.slice(0, 4)
      : view === 'month'
        ? ''
        : strings.appointment.calendarWeek(isoWeek(anchor))

  /** Paging. A month moves by a month — stepping 42 days would drift, and
   *  after two presses the header would name a month the grid is not on. */
  const step = (direction: 1 | -1) =>
    setSearch({
      date:
        view === 'month' ? shiftMonth(anchor, direction) : addDays(anchor, direction * dayCount),
    })

  return (
    /* `h-full`, not a viewport calculation, and no negative margin: the shell
       gives this route no padding (`lib/page-chrome.ts`) and `main` is now a
       bounded flex child, so the calendar simply fills it. The `-m-8` dated
       from before K1 took the padding away and had been pulling the toolbar
       32px off the top-left corner ever since; the `100svh − 3.5rem` was the
       same sum written out by hand, and it had to be wrong the moment anything
       above it changed height. Found in K6, when the shell started owning the
       scroll. */
    <div className="flex h-full min-w-0">
      <CalendarSidebar
        anchor={overviewDay}
        // The band says which days the grid is showing. In the month view that
        // is the whole month, and a band over every row says nothing — the
        // mini month is already on that month.
        visible={view === 'month' ? new Set<string>() : new Set(days)}
        occupied={occupied}
        onNew={() => setSelection({ kind: 'new' })}
        onPickDay={(date: string) => setSearch({ date })}
        finder={
          <SlotFinder
            search={slotSearch}
            result={freeSlots.data}
            onSearch={(next) => {
              setSlotSearch(next)
              // The answer is times on days, and only the grid can show it.
              // Starting a search from the month or the list therefore goes
              // where the answer can be seen, rather than leaving the finder
              // running against a view that cannot paint it.
              if (view === 'month' || view === 'list') setSearch({ view: undefined })
            }}
            onClear={() => setSlotSearch(null)}
          />
        }
      />

      <section className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-3 border-b bg-card px-5 py-3">
          <Button variant="outline" size="sm" onClick={() => setSearch({ date: undefined })}>
            {strings.appointment.today}
          </Button>
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label={strings.appointment.previous}
              onClick={() => step(-1)}
            >
              <ChevronLeft className="size-4" aria-hidden />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label={strings.appointment.next}
              onClick={() => step(1)}
            >
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          </div>
          <h1 className="whitespace-nowrap font-semibold text-[19px] tracking-[-0.015em]">
            {title}
          </h1>
          <span className="whitespace-nowrap text-[13px] text-muted-foreground tabular-nums">
            {subtitle}
          </span>

          <div className="ml-auto flex items-center gap-0.5 rounded-lg border p-0.5">
            {VIEWS.map((value) => (
              <Button
                key={value}
                size="sm"
                variant={view === value ? 'default' : 'ghost'}
                className="h-7"
                onClick={() => setSearch({ view: value === 'workweek' ? undefined : value })}
              >
                {strings.appointment.views[value]}
              </Button>
            ))}
          </div>
        </div>

        {conflicts.data && conflicts.data.length > 0 && (
          <div className="px-5 pt-3">
            <SyncConflictBanner conflicts={conflicts.data} />
          </div>
        )}

        {/* One of three. The month and the list are not the grid with other
            numbers — they answer different questions and share only the entries
            and what happens when one is clicked. */}
        {view === 'month' ? (
          <CalendarMonth
            days={days}
            month={anchor.slice(0, 7)}
            entries={shown}
            types={types.data}
            selectedId={selectedEntryId}
            onSelectEntry={selectEntry}
            onPickDay={(date) => setSearch({ date, view: 'day' })}
          />
        ) : view === 'list' ? (
          <CalendarList
            days={days}
            entries={shown}
            types={types.data}
            selectedId={selectedEntryId}
            onSelectEntry={selectEntry}
          />
        ) : (
          <CalendarGrid
            days={days}
            view={view}
            entries={shown}
            busy={busy.data ?? []}
            openingHours={openingHours.data ?? []}
            types={types.data}
            conflicted={conflicted}
            selectedId={selectedEntryId}
            onSelect={selectEntry}
            onNewAt={(startsAtLocal) => setSelection({ kind: 'new', startsAtLocal })}
            onMove={(target) => move.mutate(target)}
            draft={draft}
            draftClashes={clashes}
            {...(slotSearch
              ? {
                  freeSlots: freeSlots.data?.slots ?? [],
                  freeSlotsAreComplete: freeSlots.data?.privateCalendarsChecked ?? true,
                  slotTypeLabel: slotSearch.typeId
                    ? activityTypeLabel(types.data, slotSearch.typeId)
                    : null,
                  onPickSlot: pickSlot,
                }
              : {})}
          />
        )}
      </section>

      <CalendarRail
        anchor={overviewDay}
        nextFree={
          nextFreeSearch
            ? {
                slot: (nextFree.data?.slots ?? [])[0] ?? null,
                durationMin: nextFreeSearch.durationMin,
                typeLabel: nextFreeSearch.typeId
                  ? activityTypeLabel(types.data, nextFreeSearch.typeId)
                  : null,
              }
            : null
        }
        onUseNextFree={(slot) =>
          setSelection({
            kind: 'new',
            startsAtLocal: toBerlinDateTimeLocal(slot.startsAt).slice(0, 16),
            ...(nextFreeSearch ? { durationMin: nextFreeSearch.durationMin } : {}),
            ...(nextFreeSearch?.typeId ? { typeId: nextFreeSearch.typeId } : {}),
            mode: nextFreeSearch?.typeId ? 'activity' : 'appointment',
          })
        }
        entries={shown}
        selection={selection}
        onSelectEntry={selectEntry}
        draft={draft}
        onDraftChange={setDraft}
        warning={
          clashes ? (
            <p className="mt-4 text-destructive text-sm leading-snug">
              {strings.appointment.clashHint}
            </p>
          ) : null
        }
        onClose={() => setSelection(null)}
        onSaved={() => {
          // Entered — which is what the search was for, so it ends here.
          setSelection(null)
          setSlotSearch(null)
        }}
      />
    </div>
  )
}
